import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Rocket, Star, TrendingUp, Sparkles } from "lucide-react";

const FALLBACK_TIERS = [
  { id: "boost_7",  days: 7,  price: 99,  label: "7 days",  badge: "Kickstart",    perDay: 14.14 },
  { id: "boost_15", days: 15, price: 199, label: "15 days", badge: "Most popular", perDay: 13.27 },
  { id: "boost_30", days: 30, price: 250, label: "30 days", badge: "Best value",   perDay: 8.33 },
];

/**
 * Modal shown right after a fresh property listing is submitted.
 * Offers the owner three Boost tiers so their listing appears on top
 * of search + city landing pages.
 *
 * Silently hides itself if Boost is disabled globally (BOOST_ENABLED=false),
 * so we never advertise a checkout that's turned off.
 */
export default function BoostUpsellModal({ open, onClose, propertyId, propertyTitle }) {
  const nav = useNavigate();
  const [enabled, setEnabled] = useState(true);
  const [tiers, setTiers] = useState(FALLBACK_TIERS);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    // Check whether Boost is on
    api.get("/config").then(({ data }) => {
      setEnabled(!!data.boost_enabled);
    }).catch(() => {});
    // Pull live pricing from backend (falls back to hard-coded if it fails)
    api.get("/payments/boost-packages").then(({ data }) => {
      const list = Object.entries(data || {}).map(([id, p]) => ({
        id,
        days: p.featured_days || 7,
        price: p.amount || 0,
        label: p.name?.replace(/^Boost /, "") || `${p.featured_days || 7} days`,
        badge: id === "boost_15" ? "Most popular" : id === "boost_30" ? "Best value" : "Kickstart",
        perDay: p.amount && p.featured_days ? p.amount / p.featured_days : 0,
      })).sort((a, b) => a.days - b.days);
      if (list.length) setTiers(list);
    }).catch(() => {});
  }, [open]);

  // Nothing to sell if Boost is off — quietly skip the modal
  if (!open || !enabled) return null;

  const goToCheckout = async (tier) => {
    setBusy(true);
    try {
      // BoostPage lets the owner pick a tier and pay via UPI QR.
      const params = new URLSearchParams({ prop: propertyId, tier: tier.id });
      onClose?.();
      nav(`/boost?${params.toString()}`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const skip = () => {
    onClose?.();
    // Fall through to the property page.
    nav(`/property/${propertyId}`);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && skip()}>
      <DialogContent
        className="max-w-2xl rounded-2xl"
        data-testid="boost-upsell-modal"
      >
        <DialogHeader>
          <DialogTitle className="font-display text-2xl brand-forest flex items-center gap-2">
            <Rocket size={22} className="text-[#C9A24A]" />
            Get 10× more views on your listing
          </DialogTitle>
        </DialogHeader>

        <p className="text-sm text-gray-600 -mt-2">
          <strong className="brand-forest">{propertyTitle || "Your listing"}</strong> is live!
          Feature it at the top of search results &amp; your city landing page so
          <strong> serious tenants see it first</strong>.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-2">
          {tiers.map((t) => {
            const popular = t.id === "boost_15";
            return (
              <button
                key={t.id}
                data-testid={`boost-tier-${t.id}`}
                onClick={() => goToCheckout(t)}
                disabled={busy}
                className={`text-left border-2 rounded-2xl p-4 transition-all hover:-translate-y-0.5 hover:shadow-lg disabled:opacity-50 ${
                  popular
                    ? "border-[#C9A24A] bg-[#FFFBEE]"
                    : "border-brand-subtle bg-white hover:border-[#C9A24A]"
                }`}
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[10px] font-bold uppercase tracking-widest text-[#C9A24A] flex items-center gap-1">
                    {popular ? <Star size={10} fill="#C9A24A" /> : <TrendingUp size={10} />}
                    {t.badge}
                  </span>
                  {popular && (
                    <span className="text-[10px] bg-[#C9A24A] text-white rounded-full px-2 py-0.5 font-bold">
                      Popular
                    </span>
                  )}
                </div>
                <p className="font-display text-2xl brand-forest">{t.label}</p>
                <p className="text-3xl font-bold text-gray-900 mt-1">
                  ₹{t.price.toLocaleString("en-IN")}
                </p>
                {t.perDay > 0 && (
                  <p className="text-xs text-gray-500">₹{t.perDay.toFixed(0)}/day</p>
                )}
                <ul className="text-xs text-gray-600 mt-3 space-y-1">
                  <li>✓ Top of search for {t.days} days</li>
                  <li>✓ Gold "Featured" seal on card</li>
                  <li>✓ Priority on city page</li>
                </ul>
              </button>
            );
          })}
        </div>

        <div className="mt-3 flex items-center justify-between text-xs text-gray-500">
          <p className="flex items-center gap-1">
            <Sparkles size={12} className="text-[#C9A24A]" />
            Instant checkout · Cancel anytime
          </p>
          <button
            onClick={skip}
            data-testid="boost-upsell-skip"
            className="text-gray-500 hover:text-[#7A1E2A] underline underline-offset-2"
          >
            Skip for now
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
